// ============ GURU TRADES — dashboard card ============
// Latest US Senate / House / corporate-insider dealings (FMP disclosure
// feeds via /api/gurutrades). Adapted from NOPOLY's "guru board" idea,
// rendered in Master of the Market's own card/table style. The card hides
// itself when the feed is empty (e.g. no FMP key).
import React, { useEffect, useState } from 'react';
import { SecHead } from './core';
import { useT, useLang } from '../lib/i18n';

interface GuruTrade {
  sym: string; name: string; role: string; side: 'buy' | 'sell' | 'other';
  amount: string; txDate: string; disclosed: string; link: string | null;
}

const TABS: [string, string, string][] = [
  ['senate', 'สภาสูง (สว.)', 'Senate'],
  ['house', 'สภาผู้แทน (สส.)', 'House'],
  ['insider', 'ผู้บริหาร/วงใน', 'Insiders'],
  ['gurus', 'พอร์ตกูรู', 'Guru Portfolios'],
];

// Quarterly 13F snapshot of famous investors (SEC filings + May-2026 congress
// disclosures). Editorial summary — update each 13F season (filed ~45 days
// after quarter end). Q1 2026, filed 15 May 2026.
const GURU_ASOF = 'Q1 2026';
const GURU_PORTFOLIOS = [
  { name: 'Warren Buffett', fund: 'Berkshire · $263B', top: ['AAPL', 'AXP', 'KO', 'BAC', 'CVX'], buy: ['GOOGL', 'DAL', 'NYT', 'LEN'], sell: ['CVX −35%', 'ปิด 16 ตัว'] },
  { name: 'Nancy Pelosi', fund: 'เทรดปี 2026 · $8.9M', top: ['NVDA', 'GOOGL', 'AVGO', 'AAPL', 'PANW'], buy: ['INTC calls', 'UBER calls'], sell: [] },
  { name: 'Cathie Wood', fund: 'ARK · ~$16B', top: ['TSLA', 'COIN', 'PLTR', 'HOOD'], buy: ['SNOW', 'AMZN', 'CRCL'], sell: ['ROKU', 'BABA', 'BIDU'] },
  { name: 'Michael Burry', fund: 'Scion · ฝั่ง SHORT', top: [], buy: [], sell: ['NVDA', 'TSLA', 'MU', 'CAT', 'SOXX'] },
  { name: 'Bill Ackman', fund: 'Pershing · $13.7B', top: ['BN', 'AMZN', 'UBER'], buy: ['MSFT $2.1B'], sell: ['GOOGL เกือบหมด'] },
  { name: 'S. Druckenmiller', fund: 'Duquesne · $3.4B', top: [], buy: ['AVGO', 'SNDK', 'STX', 'MU', 'BE'], sell: ['GOOGL', 'ปิด 23 ตัว'] },
  { name: 'David Tepper', fund: 'Appaloosa · ~$6B', top: ['AMZN'], buy: ['AMZN ×2'], sell: [] },
  { name: 'Ray Dalio', fund: 'Bridgewater · $22.4B', top: [], buy: ['NVDA', 'AVGO', 'MU', 'TSM'], sell: ['CRM', 'NOW', 'WDAY', 'ADBE'] },
  { name: 'George Soros', fund: 'Soros Fund · $7.4B', top: ['AMZN', 'EA', 'NVDA', 'TSM', 'GOOGL'], buy: ['AMZN', 'NVDA', 'TSM'], sell: [] },
  { name: 'Ken Griffin', fund: 'Citadel', top: [], buy: ['AMZN ~$3.2B', 'NVDA ~$4B'], sell: [] },
];

export function GuruTradesCard() {
  const t = useT();
  const { lang } = useLang();
  const [tab, setTab] = useState('senate');
  const [rows, setRows] = useState<Record<string, GuruTrade[]>>({});

  useEffect(() => {
    if (tab === 'gurus' || rows[tab]) return; // gurus tab is editorial, no fetch
    let alive = true;
    fetch(`/api/gurutrades?type=${tab}`).then(r => (r.ok ? r.json() : []))
      .then(d => { if (alive) setRows(x => ({ ...x, [tab]: Array.isArray(d) ? d : [] })); })
      .catch(() => { if (alive) setRows(x => ({ ...x, [tab]: [] })); });
    return () => { alive = false; };
  }, [tab, rows]);

  const list = rows[tab];
  if (rows.senate && rows.senate.length === 0 && tab === 'senate') return null; // no key / feed down

  const fmtD = (iso: string) => (iso ? new Date(iso).toLocaleDateString(lang === 'en' ? 'en-US' : 'th-TH', { day: 'numeric', month: 'short' }) : '—');

  return (
    <div className="mm-card glass mm-guru">
      <div className="mm-guru-head">
        <SecHead th="ธุรกรรมวงใน · GURU TRADES" en="GURU TRADES · CONGRESS & INSIDERS"></SecHead>
        <div className="mm-guru-tabs">
          {TABS.map(([id, th, en]) => (
            <button key={id} className={`mm-pill ${tab === id ? 'on' : ''}`} onClick={() => setTab(id)}>{t(th, en)}</button>
          ))}
        </div>
      </div>
      {tab === 'gurus' && (
        <React.Fragment>
          <div className="mm-guru-grid">
            {GURU_PORTFOLIOS.map(g => (
              <div className="mm-guru-cardlet" key={g.name}>
                <div className="mm-guru-cardlet-h"><b>{g.name}</b><span className="dim mono">{g.fund}</span></div>
                {g.top.length > 0 && (
                  <div className="mm-guru-line"><span className="mm-std-lbl mono">{t('ถือหลัก', 'TOP')}</span>
                    {g.top.map(s => <span key={s} className="mm-chip dim mono">{s}</span>)}</div>
                )}
                {g.buy.length > 0 && (
                  <div className="mm-guru-line"><span className="mm-std-lbl mono up">{t('ซื้อ', 'BUY')}</span>
                    {g.buy.map(s => <span key={s} className="mm-chip mono up">{s}</span>)}</div>
                )}
                {g.sell.length > 0 && (
                  <div className="mm-guru-line"><span className="mm-std-lbl mono down">{g.fund.includes('SHORT') ? t('ชอร์ต', 'SHORT') : t('ขาย', 'SELL')}</span>
                    {g.sell.map(s => <span key={s} className="mm-chip mono down">{s}</span>)}</div>
                )}
              </div>
            ))}
          </div>
          <p className="mm-footnote dim">
            {t(`ข้อมูล 13F ไตรมาสล่าสุด (${GURU_ASOF} ยื่น 15 พ.ค. 2026) + เอกสารเปิดเผยของคองเกรส — 13F รายงานย้อนหลัง ~45 วัน สถานะปัจจุบันอาจเปลี่ยนแล้ว`,
              `Latest 13F snapshot (${GURU_ASOF}, filed May 15, 2026) + congressional disclosures — 13F lags ~45 days; current positions may differ`)}
          </p>
        </React.Fragment>
      )}
      {tab !== 'gurus' && (
      <React.Fragment>
      <div style={{ overflowX: 'auto' }}>
        <table className="mm-table">
          <thead><tr>
            <th>{t('หุ้น', 'Ticker')}</th><th>{t('ผู้ทำรายการ', 'Who')}</th>
            <th>{t('ฝั่ง', 'Side')}</th><th>{t('มูลค่า', 'Amount')}</th>
            <th>{t('วันเทรด', 'Traded')}</th><th>{t('เปิดเผย', 'Filed')}</th>
          </tr></thead>
          <tbody>
            {(list || []).slice(0, 10).map((r, i) => (
              <tr key={i}>
                <td><b className="mono">{r.sym}</b></td>
                <td>
                  {r.link ? <a href={r.link} target="_blank" rel="noopener noreferrer" className="mm-guru-name">{r.name} ↗</a> : r.name}
                  {r.role && <span className="dim" style={{ marginLeft: 6, fontSize: '0.72rem' }}>{r.role}</span>}
                </td>
                <td><span className={`mm-chip mono ${r.side === 'buy' ? 'up' : r.side === 'sell' ? 'down' : 'dim'}`}>
                  {r.side === 'buy' ? t('ซื้อ', 'BUY') : r.side === 'sell' ? t('ขาย', 'SELL') : '—'}</span></td>
                <td className="mono dim">{r.amount}</td>
                <td className="dim">{fmtD(r.txDate)}</td>
                <td className="dim">{fmtD(r.disclosed)}</td>
              </tr>
            ))}
            {list && list.length === 0 && (
              <tr><td colSpan={6} className="dim" style={{ padding: 12 }}>{t('ยังไม่มีข้อมูลชุดนี้', 'No data for this feed yet')}</td></tr>
            )}
            {!list && <tr><td colSpan={6} className="dim" style={{ padding: 12 }}>{t('กำลังโหลด…', 'Loading…')}</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="mm-footnote dim">
        {t('ที่มา: เอกสารเปิดเผยต่อ ก.ล.ต./สภาคองเกรส (ผ่าน FMP) — มีดีเลย์ตามกฎหมายเปิดเผย ไม่ใช่สัญญาณเรียลไทม์ · คลิกชื่อเพื่อดูเอกสารต้นฉบับ',
          'Source: SEC / Congress disclosure filings (via FMP) — legally delayed, not a real-time signal · click a name for the original filing')}
      </p>
      </React.Fragment>
      )}
    </div>
  );
}
